import { type ReactNode, useCallback } from "react";
import css from "../orgList.module.scss";

/* ── Member / group row (design .mrow) ── */

type MemberRowProps = {
  /** Optional leading select checkbox. */
  checkbox?: ReactNode;
  /** Leading status / avatar visual (rendered inside the 30px status box). */
  status?: ReactNode;
  /** Background tint of the 30px status box. */
  statusTint?: string;
  name: ReactNode;
  sub?: ReactNode;
  /** Profile labels row (chips), shown under name/sub. */
  labels?: ReactNode;
  /** Trailing actions (e.g. RowMenu). */
  actions?: ReactNode;
  selected?: boolean;
  onClick?: () => void;
};

export function MemberRow({
  checkbox,
  status,
  statusTint,
  name,
  sub,
  labels,
  actions,
  selected,
  onClick,
}: MemberRowProps) {
  const handleKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (onClick && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const className = [css.mrow, onClick && css.clk, selected && css.sel].filter(Boolean).join(" ");

  return (
    <div
      className={className}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? handleKey : undefined}
    >
      {checkbox}
      {status != null && (
        <span className={css.status} style={statusTint ? { background: statusTint } : undefined}>
          {status}
        </span>
      )}
      <div className={css.main}>
        <div className={css.name}>{name}</div>
        {sub != null && sub !== "" && <div className={css.sub}>{sub}</div>}
        {labels != null && <div className={css.labels}>{labels}</div>}
      </div>
      {actions}
    </div>
  );
}

/** A list card body that holds member rows, with the design's empty state. */
export function MemberList({
  isEmpty,
  emptyTitle,
  emptySub,
  children,
}: {
  isEmpty: boolean;
  emptyTitle: string;
  emptySub?: string;
  children: ReactNode;
}) {
  if (isEmpty) {
    return (
      <div style={{ textAlign: "center", padding: "50px 24px" }}>
        <div style={{ fontSize: 17 }}>{emptyTitle}</div>
        {emptySub && (
          <div
            style={{
              fontSize: 13,
              color: "var(--color-grey)",
              margin: "8px auto 0",
              maxWidth: 420,
              lineHeight: 1.5,
            }}
          >
            {emptySub}
          </div>
        )}
      </div>
    );
  }
  return <>{children}</>;
}
